/**
 * Helpers for extracting and formatting shipment display data.
 * Used by ShipmentCard to transform GraphQL shipment/order data into display-ready values.
 */
import { GraphQL } from "@rpi/openapi-api";

type Shipment = NonNullable<NonNullable<GraphQL.OrderQuery["orderByIdentifier"]>["shipments"]>[number];
type Address = NonNullable<NonNullable<Shipment["destination"]>["address"]>;

export type { Shipment, Address };

const NOT_PROVIDED = "-";

export function hasTrackingInfo(shipment: Shipment): boolean {
  return !!(shipment.trackingNumber || shipment.masterTrackingNumber || shipment.secondaryTrackingNumber);
}

export function getTrackingNumber(shipment: Shipment): string | null {
  return shipment.trackingNumber ?? shipment.masterTrackingNumber ?? shipment.secondaryTrackingNumber ?? null;
}

/**
 * Per Figma spec:
 * - Shipped (has tracking): "[Method] - ([tracking#])"
 * - Not shipped (no tracking): "-"
 *
 * Uses the human-readable shipMethodName from shippingDetails when available,
 * falls back to the raw shippingMethodCode.
 */
export function formatShippingMethodDisplay(shipment: Shipment): { method: string; trackingNumber: string | null } {
  const trackingNumber = getTrackingNumber(shipment);
  if (!trackingNumber) {
    return { method: NOT_PROVIDED, trackingNumber: null };
  }
  const method = resolveCarrierName(shipment) || NOT_PROVIDED;
  return { method, trackingNumber };
}

/**
 * Parses estimatedDeliveryDateUTC from the shipment's destination shippingDetails JSON.
 * Formats as M/D/YYYY per Figma. Returns "-" when unavailable.
 */
export function formatEstimatedDeliveryDate(shipment: Shipment): string {
  const dateString = parseShippingDetail<string>(shipment, "estimatedDeliveryDateUTC");
  if (!dateString) return NOT_PROVIDED;

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return NOT_PROVIDED;

  return formatDateMDYYYY(date);
}

export function formatContactName(address: Address): string {
  return address.name ?? NOT_PROVIDED;
}

export function formatContactEmail(address: Address): string {
  return address.email ?? NOT_PROVIDED;
}

export function formatContactPhone(address: Address): string {
  return address.phone ?? NOT_PROVIDED;
}

/** Returns [recipientName, formattedAddress] for two-line display per Figma. */
export function formatShippingAddressLines(address: Address): string[] {
  const name = address.name ?? NOT_PROVIDED;
  const addressParts = [
    address.address1,
    address.address2,
    address.address3,
    address.city,
    address.regionIso,
    address.postalCode,
    address.countryIso,
  ].filter(Boolean);

  const addressLine = addressParts.length > 0 ? addressParts.join(", ") : NOT_PROVIDED;
  return [name, addressLine];
}

/**
 * Carrier tracking URL builders. Each entry maps a lowercase carrier keyword to a URL
 * builder using the carrier's query parameter for the tracking number.
 *
 * URL formats sourced from carrier documentation and verified manually:
 *   FedEx:  https://eshipguy.com/tracking/ → param `trknbr`
 *   UPS:    https://www.ups.com/us/en/business-shipping/shipping/tracking-api.page → param `tracknum`
 *   USPS:   https://tools.usps.com/go/TrackConfirmAction → param `qtc_tLabels1`
 *   DHL:    https://www.dhl.com/us-en/home/tracking.html — param `tracking-id` manually verified;
 *           no official deep-link documentation found. If links break, check current URL format.
 *
 * When updating a URL, also update the corresponding assertion in shipmentHelpers.test.ts.
 */
const CARRIER_TRACKING_URLS = new Map<string, (trackingNumber: string) => string>([
  ["fedex", (tn) => `https://www.fedex.com/fedextrack/?trknbr=${tn}`],
  ["ups", (tn) => `https://www.ups.com/track?tracknum=${tn}`],
  ["usps", (tn) => `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${tn}`],
  ["dhl", (tn) => `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${tn}`],
]);

/**
 * Builds a tracking URL by matching the carrier name against known carriers.
 * Uses shippingDetails.shipMethodName when available, falls back to shippingMethodCode.
 * Returns null if carrier is unrecognized or tracking number is absent.
 */
export function buildTrackingUrl(shipment: Shipment): string | null {
  const trackingNumber = getTrackingNumber(shipment);
  if (!trackingNumber) return null;

  const carrierName = resolveCarrierName(shipment).toLowerCase();
  for (const [carrier, buildUrl] of CARRIER_TRACKING_URLS) {
    if (carrierName.includes(carrier)) {
      return buildUrl(trackingNumber);
    }
  }
  return null;
}

/** Resolves a human-readable carrier name, preferring shippingDetails then falling back to shippingMethodCode. */
function resolveCarrierName(shipment: Shipment): string {
  return getShipMethodName(shipment) ?? shipment.shippingMethodCode ?? "";
}

function getShipMethodName(shipment: Shipment): string | null {
  return parseShippingDetail<string>(shipment, "shipMethodName") ?? null;
}

function parseShippingDetail<T>(shipment: Shipment, key: string): T | undefined {
  const shippingDetails: unknown = shipment.destination?.shippingDetails;
  if (!shippingDetails || typeof shippingDetails !== "object") return undefined;
  return (shippingDetails as Record<string, unknown>)[key] as T | undefined;
}

function formatDateMDYYYY(date: Date): string {
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  return `${month}/${day}/${year}`;
}
